/**
 * Human-reviewable trade pack content for the Improvement Kit.
 * Avoid unsupported claims; FAQ/process seeds use [Add ...] where the owner must confirm.
 * Reviewed for launch gate (Spec 7.3 / K-02).
 */

import type { TradePackId } from './types';
import { lintContentBundle } from './claim-linter';

export type TradePack = {
  id: TradePackId;
  label: string;
  /** Example service names (owner picks one per run). */
  services: string[];
  /** Default process steps when owner leaves process blank. */
  processSteps: string[];
  /** FAQ question seeds; answers use [Add ...] so they never invent facts. */
  faqSeeds: Array<{ q: string; a: string }>;
  /** Suggested photo filenames + alt examples (adapted by owner). */
  imageSuggestions: Array<{ filename: string; alt: string }>;
  /** CTA button copy variants (may include {phone} / {business}). */
  ctaVariants: string[];
  /** Schema.org LocalBusiness subtype when known. */
  schemaType: string;
};

const PACKS: TradePack[] = [
  {
    id: 'plumbing',
    label: 'Plumbing',
    services: ['Drain cleaning', 'Water heater repair', 'Leak repair', 'Fixture install', 'Sewer line'],
    processSteps: [
      'Tell us what is going on and when it started',
      'We confirm timing and what to expect on site',
      'We diagnose the issue and explain options before work begins',
      'We complete the repair and walk you through what we did',
    ],
    faqSeeds: [
      { q: 'Do you offer estimates before starting work?', a: '[Add your estimate policy]' },
      { q: 'Which areas do you serve?', a: '[Add your service area]' },
      { q: 'Are you licensed?', a: '[Add license details if you want them shown]' },
      { q: 'How quickly can you come out?', a: '[Add typical response timing]' },
      { q: 'What should I do if I have a leak right now?', a: '[Add emergency shutoff guidance you are comfortable sharing]' },
    ],
    imageSuggestions: [
      { filename: 'water-heater-replacement.jpg', alt: 'Technician installing a water heater' },
      { filename: 'drain-cleaning-van.jpg', alt: 'Plumbing service van ready for a drain cleaning call' },
      { filename: 'fixture-install.jpg', alt: 'Newly installed bathroom faucet' },
    ],
    ctaVariants: ['Request an Estimate', 'Call {phone}', 'Schedule a Plumbing Visit', 'Get Help With This Issue', 'Ask About Availability'],
    schemaType: 'Plumber',
  },
  {
    id: 'hvac',
    label: 'HVAC',
    services: ['AC repair', 'Furnace tune-up', 'System replacement', 'Duct cleaning', 'Thermostat install'],
    processSteps: [
      'Share the symptoms and your system type if you know it',
      'We schedule a visit that fits your home',
      'We inspect, explain findings, and outline options',
      'We complete agreed work and confirm everything is running',
    ],
    faqSeeds: [
      { q: 'Do you service both heating and cooling?', a: '[Add which systems you service]' },
      { q: 'Do you offer maintenance plans?', a: '[Add maintenance plan details or leave blank]' },
      { q: 'Which brands do you work on?', a: '[Add brands you support]' },
      { q: 'How long does a typical repair visit take?', a: '[Add typical visit length]' },
      { q: 'Are your technicians background-checked?', a: '[Add hiring / screening facts you can stand behind]' },
    ],
    imageSuggestions: [
      { filename: 'ac-condenser-service.jpg', alt: 'Technician servicing an outdoor AC condenser' },
      { filename: 'furnace-inspection.jpg', alt: 'Technician inspecting a home furnace' },
      { filename: 'thermostat-install.jpg', alt: 'New thermostat mounted on an interior wall' },
    ],
    ctaVariants: ['Request an Estimate', 'Call {phone}', 'Book an HVAC Check', 'Ask About Repair Options', 'Schedule Service'],
    schemaType: 'HVACBusiness',
  },
  {
    id: 'electrical',
    label: 'Electrical',
    services: ['Panel upgrade', 'Outlet and switch install', 'Lighting', 'EV charger', 'Troubleshooting'],
    processSteps: [
      'Describe the electrical issue or project goal',
      'We review safety considerations and schedule a visit',
      'We inspect and explain recommended work',
      'We complete the job and test circuits before we leave',
    ],
    faqSeeds: [
      { q: 'Are you a licensed electrician?', a: '[Add license details if you want them shown]' },
      { q: 'Do you pull permits when required?', a: '[Add your permit practice]' },
      { q: 'Can you install EV chargers?', a: '[Add EV charger capability]' },
      { q: 'Do you handle both homes and light commercial?', a: '[Add customer types you serve]' },
      { q: 'What should I do if breakers keep tripping?', a: '[Add safe guidance you are comfortable sharing]' },
    ],
    imageSuggestions: [
      { filename: 'panel-upgrade.jpg', alt: 'Electrician working on a residential panel' },
      { filename: 'outlet-install.jpg', alt: 'New outlet installed in a finished wall' },
      { filename: 'lighting-install.jpg', alt: 'Ceiling light fixture installation' },
    ],
    ctaVariants: ['Request an Estimate', 'Call {phone}', 'Schedule Electrical Service', 'Ask About Panel Work', 'Get a Site Visit'],
    schemaType: 'Electrician',
  },
  {
    id: 'roofing',
    label: 'Roofing',
    services: ['Roof repair', 'Inspection', 'Replacement', 'Storm damage assessment', 'Gutter work'],
    processSteps: [
      'Tell us what you noticed (leak, missing shingles, age of roof)',
      'We schedule an inspection',
      'We document findings and explain repair vs replace options',
      'We complete agreed work and do a final walkthrough',
    ],
    faqSeeds: [
      { q: 'Do you offer free inspections?', a: '[Add inspection policy]' },
      { q: 'What roofing materials do you install?', a: '[Add materials you install]' },
      { q: 'Do you help with insurance claims?', a: '[Add insurance documentation support]' },
      { q: 'How long does a full replacement take?', a: '[Add typical project timeline]' },
      { q: 'Do you clean up daily?', a: '[Add cleanup practice]' },
    ],
    imageSuggestions: [
      { filename: 'roof-inspection.jpg', alt: 'Roofer inspecting shingles on a residential roof' },
      { filename: 'shingle-repair.jpg', alt: 'Close-up of shingle repair in progress' },
      { filename: 'roof-replacement.jpg', alt: 'Roof replacement materials staged on a home' },
    ],
    ctaVariants: ['Request an Estimate', 'Call {phone}', 'Schedule a Roof Inspection', 'Ask About Repairs', 'Get Storm Damage Help'],
    schemaType: 'RoofingContractor',
  },
  {
    id: 'landscaping',
    label: 'Landscaping',
    services: ['Lawn care', 'Mulch and beds', 'Hardscape', 'Seasonal cleanup', 'Irrigation'],
    processSteps: [
      'Share photos or a short description of the outdoor project',
      'We confirm scope and timing for your property',
      'We outline the plan and materials',
      'We complete the work and review the finished space with you',
    ],
    faqSeeds: [
      { q: 'Do you offer weekly lawn service?', a: '[Add recurring service options]' },
      { q: 'Which cities or neighborhoods do you cover?', a: '[Add your service area]' },
      { q: 'Do you provide materials or use what I supply?', a: '[Add materials policy]' },
      { q: 'Can you design a new bed or patio?', a: '[Add design / hardscape capability]' },
      { q: 'Do you work year-round?', a: '[Add seasonal availability]' },
    ],
    imageSuggestions: [
      { filename: 'lawn-maintenance.jpg', alt: 'Freshly maintained residential lawn' },
      { filename: 'mulch-beds.jpg', alt: 'Landscaped mulch beds along a walkway' },
      { filename: 'hardscape-patio.jpg', alt: 'Completed patio hardscape' },
    ],
    ctaVariants: ['Request an Estimate', 'Call {phone}', 'Plan Outdoor Work', 'Ask About Lawn Care', 'Schedule a Visit'],
    schemaType: 'LocalBusiness',
  },
  {
    id: 'painting',
    label: 'Painting',
    services: ['Interior painting', 'Exterior painting', 'Cabinet refinishing', 'Deck staining', 'Color consult'],
    processSteps: [
      'Tell us rooms or surfaces and preferred timing',
      'We visit or review photos to confirm prep needs',
      'We share a clear scope and color plan',
      'We prep, paint, and leave the space ready to use',
    ],
    faqSeeds: [
      { q: 'Do you move furniture and cover floors?', a: '[Add prep / protection practice]' },
      { q: 'What paint brands do you prefer?', a: '[Add paint brands you use]' },
      { q: 'Do you handle exterior pressure washing?', a: '[Add exterior prep details]' },
      { q: 'How long before rooms can be used again?', a: '[Add dry / re-entry timing]' },
      { q: 'Do you help choose colors?', a: '[Add color consult option]' },
    ],
    imageSuggestions: [
      { filename: 'interior-room-paint.jpg', alt: 'Freshly painted living room walls' },
      { filename: 'exterior-trim-paint.jpg', alt: 'Painter finishing exterior trim' },
      { filename: 'cabinet-refinish.jpg', alt: 'Kitchen cabinets after refinishing' },
    ],
    ctaVariants: ['Request an Estimate', 'Call {phone}', 'Schedule a Painting Visit', 'Ask About Interior Work', 'Get a Color Plan'],
    schemaType: 'HousePainter',
  },
  {
    id: 'remodeling',
    label: 'Remodeling',
    services: ['Kitchen remodel', 'Bath remodel', 'Basement finish', 'Room addition', 'Accessibility updates'],
    processSteps: [
      'Share goals, photos, and a rough budget range if you have one',
      'We discuss scope, timeline, and decision points',
      'We provide a written outline of work and selections',
      'We build in phases and keep you updated through completion',
    ],
    faqSeeds: [
      { q: 'Do you manage permits and inspections?', a: '[Add permit / GC practice]' },
      { q: 'Can you work from my designer plans?', a: '[Add design collaboration approach]' },
      { q: 'How do change orders work?', a: '[Add change-order policy]' },
      { q: 'Do you provide temporary kitchen or bath setups?', a: '[Add temporary living arrangements if any]' },
      { q: 'What is a typical project timeline?', a: '[Add timeline ranges you can stand behind]' },
    ],
    imageSuggestions: [
      { filename: 'kitchen-remodel.jpg', alt: 'Completed kitchen remodel' },
      { filename: 'bath-remodel.jpg', alt: 'Updated bathroom vanity and tile' },
      { filename: 'framing-progress.jpg', alt: 'Interior framing during a remodel' },
    ],
    ctaVariants: ['Request an Estimate', 'Call {phone}', 'Start a Remodel Conversation', 'Ask About Timelines', 'Schedule a Consult'],
    schemaType: 'GeneralContractor',
  },
  {
    id: 'general',
    label: 'General',
    services: ['Service call', 'Repair', 'Install', 'Maintenance', 'Estimate visit'],
    processSteps: [
      'Tell us what you need help with',
      'We confirm details and schedule a time',
      'We explain options before work begins',
      'We finish the job and review what was done',
    ],
    faqSeeds: [
      { q: 'Do you provide written estimates?', a: '[Add your estimate policy]' },
      { q: 'Which areas do you serve?', a: '[Add your service area]' },
      { q: 'Are you insured?', a: '[Add insurance details if you want them shown]' },
      { q: 'How do I prepare for the visit?', a: '[Add prep tips for customers]' },
      { q: 'How should I contact you for the fastest reply?', a: '[Add preferred contact method]' },
    ],
    imageSuggestions: [
      { filename: 'service-van.jpg', alt: 'Service van arriving at a customer property' },
      { filename: 'technician-tools.jpg', alt: 'Technician preparing tools for a job' },
      { filename: 'finished-work.jpg', alt: 'Completed repair work ready for customer review' },
    ],
    ctaVariants: ['Request an Estimate', 'Call {phone}', 'Contact Us', 'Ask a Question', 'Schedule a Visit'],
    schemaType: 'GeneralContractor',
  },
];

const BY_ID = Object.fromEntries(PACKS.map((p) => [p.id, p])) as Record<TradePackId, TradePack>;

export function listTradePacks(): TradePack[] {
  return PACKS;
}

export function getTradePack(id: string | null | undefined): TradePack {
  if (id && id in BY_ID) return BY_ID[id as TradePackId];
  return BY_ID.general;
}

/** Map free-text trade labels from onboarding onto a pack id. */
export function resolveTradePackId(trade: string | null | undefined): TradePackId {
  const t = (trade ?? '').toLowerCase();
  if (!t) return 'general';
  if (t.includes('plumb')) return 'plumbing';
  if (t.includes('hvac') || t.includes('heating') || t.includes('cooling') || t.includes('air cond')) return 'hvac';
  if (t.includes('electr')) return 'electrical';
  if (t.includes('roof')) return 'roofing';
  if (t.includes('landscap') || t.includes('lawn')) return 'landscaping';
  if (t.includes('paint')) return 'painting';
  if (t.includes('remodel') || t.includes('renovat') || t.includes('general contract')) return 'remodeling';
  const exact = PACKS.find((p) => p.id === t || p.label.toLowerCase() === t);
  return exact?.id ?? 'general';
}

/** Boot / test guard: pack content must not contain forbidden claims. */
export function assertTradePacksClean(): void {
  for (const pack of PACKS) {
    const parts = [
      ...pack.services,
      ...pack.processSteps,
      ...pack.faqSeeds.flatMap((f) => [f.q, f.a]),
      ...pack.imageSuggestions.flatMap((i) => [i.filename, i.alt]),
      ...pack.ctaVariants,
    ];
    const issues = lintContentBundle(parts);
    if (issues.length) {
      throw new Error(`Trade pack ${pack.id} has forbidden claims: ${issues.map((i) => i.label).join(', ')}`);
    }
  }
}

assertTradePacksClean();
